import React, {
  useState,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import useAnalysisStore from "../../../stores/useAnalysisStore";
import { useTranslation } from "react-i18next";
import {
  showSuccessToast,
  showErrorToast,
  showLoadingToast,
  dismissToast,
} from "../../../utils/toastUtils";
import {
  Shield,
  Plus,
  Save,
  BrainCircuit,
  Info,
  FileText,
  ChevronDown,
  Users,
  BarChart2,
} from "lucide-react";
import api from "../../../api/apiClient";
import MeasureTable from "./MeasureTable";
import MeasureForm from "./MeasureForm";
import InlineAiSuggestions from "./AIGenerationModal";
import GuideModal from "./GuideModal";
import StakeholderRiskView from "./StakeholderRiskView";
import ecosystemMeasuresService from "../../../services/ecosystemMeasuresService";
import workshop3Service from "../../../services/workshop3Service"; // Use hydration service
import EcosystemMeasuresPdfGenerator from "../../../utils/ecosystemMeasuresPdfGenerator";
import { generateEcosystemMeasuresDocx } from "../../../utils/ecosystemMeasuresDocxGenerator";
import WorkshopExportMenu from "../../common/WorkshopExportMenu";
import Button from "../../common/Button";
import { useSafeSave } from "../../../hooks/useSafeSave";
import { useAuth } from "../../../context/AuthContext";
import { useSilentSaveListener } from '../../../hooks/useSilentSaveListener';
import Workshop3Activite4CsvGenerator from "../../../utils/workshop3Activite4CsvGenerator";

const Activite4 = (props) => {
  const { currentAnalysis } = useAnalysisStore();
  const { t } = useTranslation();
  const { companyAiEnabled } = useAuth();
  const [ecosystemMeasures, setEcosystemMeasures] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [editingMeasure, setEditingMeasure] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPdfMenuOpen, setIsPdfMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("measures"); // 'measures' | 'risks'

  const {
    markDataLoaded,
    markModified,
    canAutoSave,
    clearModified,
    useFormChangeDetector,
  } = useSafeSave("Atelier3Activite4");
  const formChangeRef = useFormChangeDetector();

  // Handle silent save without toasts
  const handleSilentSave = async () => {
    if (!canAutoSave()) return "skipped";

    if (currentAnalysis?.id) {
      try {
        console.log("Activite4 - Performing silent save of ecosystem measures");
        await ecosystemMeasuresService.saveEcosystemMeasures(
          currentAnalysis.id,
          ecosystemMeasures,
        );
        clearModified();
        console.log("Activite4 - Silent save successful");
        return true;
      } catch (error) {
        console.error("Activite4 - Silent save error:", error);
        return false;
      }
    }
    return false;
  };

  useSilentSaveListener('atelier3-activite4', handleSilentSave);

  // Load existing measures
  useEffect(() => {
    if (currentAnalysis?.id) {
      loadEcosystemMeasures();
    } else {
      setIsLoading(false);
    }
  }, [currentAnalysis?.id]);

  // Load measures from API (with auto-sync and cleanup)
  const loadEcosystemMeasures = async () => {
    setIsLoading(true);
    try {
      console.log("🔄 Loading synced ecosystem measures...");
      // Use synced endpoint with auto-clean enabled
      const response = await workshop3Service.getSyncedEcosystemMeasures(
        currentAnalysis.id,
        true, // autoClean = true
      );

      if (response.success && response.data) {
        setEcosystemMeasures(response.data);

        // Log sync metadata
        if (response.metadata) {
          console.log("📊 Ecosystem Measures Sync:", {
            total: response.metadata.totalMeasures,
            valid: response.metadata.validMeasures,
            removed: response.metadata.removedMeasures,
            attackPaths: response.metadata.currentAttackPathsCount,
          });

          // Show notification if measures were auto-cleaned
          if (response.metadata.removedMeasures > 0) {
            console.warn(
              `🧹 Auto-cleaned ${response.metadata.removedMeasures} orphaned measures`,
            );
          }
        }

        markDataLoaded();
      } else {
        setEcosystemMeasures([]);
      }
    } catch (error) {
      console.error("Error loading ecosystem measures:", error);
      setEcosystemMeasures([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Save measures to API
  const saveMeasures = async () => {
    const toastId = showLoadingToast(t("workshop3.activity4.saving"));
    try {
      const response = await ecosystemMeasuresService.saveEcosystemMeasures(
        currentAnalysis.id,
        ecosystemMeasures,
      );

      if (response.success) {
        dismissToast(toastId);
        showSuccessToast(t("workshop3.activity4.success.measuresSaved"));
        clearModified();
        return true;
      } else {
        throw new Error(
          response.message || t("workshop3.activity4.errors.saveError"),
        );
      }
    } catch (error) {
      console.error("Error saving ecosystem measures:", error);
      dismissToast(toastId);
      showErrorToast(
        `${t("workshop3.activity4.errors.error")}: ${error.message || t("workshop3.activity4.errors.saveProblem")}`,
      );
      return false;
    }
  };

  // Handle AI-generated measures
  const handleAIGenerated = (generatedMeasures) => {
    // Add generated measures to existing ones
    setEcosystemMeasures((prev) => {
      // Filter out duplicates based on title
      const existingTitles = new Set(prev.map((m) => m.title));

      // Process new measures to ensure they have proper IDs and timestamps
      const newMeasures = generatedMeasures
        .filter((m) => !existingTitles.has(m.title))
        .map((measure) => ({
          ...measure,
          id:
            measure.id ||
            `measure-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          createdAt: measure.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }));

      return [...prev, ...newMeasures];
    });

    setShowAIModal(false);
    markModified();
  };

  // Add or update a measure (keepOpen = true for "Save & Add Another")
  const handleSaveMeasure = (measure, keepOpen = false) => {
    if (editingMeasure) {
      // Update existing measure
      setEcosystemMeasures((prev) =>
        prev.map((m) =>
          m.id === editingMeasure.id ? { ...measure, id: m.id } : m,
        ),
      );
    } else {
      // Add new measure
      setEcosystemMeasures((prev) => [
        ...prev,
        { ...measure, id: `measure-${Date.now()}` },
      ]);
    }

    if (!keepOpen) {
      setShowAddForm(false);
      setEditingMeasure(null);
    }
    markModified();
  };

  // Delete a measure
  const handleDeleteMeasure = (measureId) => {
    setEcosystemMeasures((prev) => prev.filter((m) => m.id !== measureId));
    markModified();
  };

  // Bulk delete measures
  const handleBulkDeleteMeasures = (measureIds) => {
    setEcosystemMeasures((prev) =>
      prev.filter((m) => !measureIds.includes(m.id)),
    );
    markModified();
  };

  const getImplementationFactor = (status) => {
    if (status === "abandoned") return 0;
    return 1.0;
  };

  const getAppliedEffectiveness = (measure) => {
    const factor = getImplementationFactor(measure.implementation);
    return {
      d: (measure.dependanceEffectiveness || 0) * factor,
      p: (measure.penetrationEffectiveness || 0) * factor,
      m: (measure.maturiteCyberEffectiveness || 0) * factor,
      c: (measure.confianceEffectiveness || 0) * factor,
    };
  };

  const calculateResidualRisks = (
    stakeholdersData,
    attackPathsData,
    measuresData,
  ) => {
    const retainedStakeholders = stakeholdersData.filter(
      (s) => Boolean(s.retained),
    );

    const getRiskZoneLabel = (threatLevel) => {
      if (threatLevel >= 3) return "Zone de danger";
      if (threatLevel >= 1.5) return "Zone de contrôle";
      if (threatLevel >= 0.5) return "Zone de veille";
      return "Hors périmètre";
    };

    return retainedStakeholders.map((stakeholder) => {
      const initialD = stakeholder.dependency || 1;
      const initialP = stakeholder.penetration || 1;
      const initialM = stakeholder.cyberMaturity || 1;
      const initialC = stakeholder.trust || 1;

      const initialRisk = (initialD * initialP) / (initialM * initialC);

      const relatedPaths = attackPathsData.filter((path) => {
        if (!path.stakeholders || path.stakeholders.length === 0) return false;
        return path.stakeholders.some((s) => {
          const sId = typeof s === "string" ? s : s.id;
          return sId === stakeholder.id || s.name === stakeholder.name;
        });
      });

      const relatedPathIds = new Set(relatedPaths.map((p) => p.id));

      const relatedMeasures = measuresData.filter((m) => {
        const mPaths =
          m.attackPaths || (m.attackPathId ? [m.attackPathId] : []);
        return mPaths.some((pid) => relatedPathIds.has(pid));
      });

      let currentD = initialD;
      let currentP = initialP;
      let currentM = initialM;
      let currentC = initialC;

      relatedMeasures.forEach((m) => {
        const eff = getAppliedEffectiveness(m);
        currentD -= eff.d;
        currentP -= eff.p;
        currentM += eff.m;
        currentC += eff.c;
      });

      currentD = Math.max(1, Math.min(4, currentD));
      currentP = Math.max(1, Math.min(4, currentP));
      currentM = Math.max(1, Math.min(4, currentM));
      currentC = Math.max(1, Math.min(4, currentC));

      const residualRisk = (currentD * currentP) / (currentM * currentC);
      const reduction =
        initialRisk > 0 ? (initialRisk - residualRisk) / initialRisk : 0;

      return {
        stakeholderName: stakeholder.name,
        initialRisk,
        residualRisk,
        initialZone: getRiskZoneLabel(initialRisk),
        residualZone: getRiskZoneLabel(residualRisk),
        reduction: Math.round(reduction * 100),
        residualRiskExpression: stakeholder.residualRiskExpression || '',
      };
    });
  };

  // Handle PDF Export (retaining original complex logic)
  const handleExportPDF = async () => {
    const toastId = showLoadingToast("Génération du rapport PDF...");
    try {
      const [pathsResponse, stakeholdersResponse] = await Promise.all([
        workshop3Service.getAttackPaths(currentAnalysis.id),
        api.get(`/analyses/${currentAnalysis.id}/atelier3/stakeholders`),
      ]);

      let attackPathsData = [];
      if (pathsResponse.success && pathsResponse.data) {
        attackPathsData = Array.isArray(pathsResponse.data)
          ? pathsResponse.data
          : [];
      }

      let stakeholdersData = [];
      if (stakeholdersResponse.success && stakeholdersResponse.data) {
        stakeholdersData = Array.isArray(stakeholdersResponse.data)
          ? stakeholdersResponse.data
          : [];
      }

      const normalizeAttackPathId = (id) => {
        if (!id) return "";
        const parts = id.split("-");
        if (parts.length > 1) {
          const lastPart = parts[parts.length - 1];
          if (lastPart.length >= 13 && /^\d+$/.test(lastPart)) {
            return parts.slice(0, -1).join("-");
          }
        }
        return id;
      };

      const attackPathIdsMatch = (id1, id2) => {
        if (!id1 || !id2) return false;
        if (id1 === id2) return true;
        const normalized1 = normalizeAttackPathId(id1);
        const normalized2 = normalizeAttackPathId(id2);
        return normalized1 === normalized2 && normalized1.length > 0;
      };

      const getAssociatedAttackPaths = (measure) => {
        if (!measure) return [];
        if (measure.attackPathsDetails && measure.attackPathsDetails.length > 0)
          return measure.attackPathsDetails;
        if (
          measure.attackPaths &&
          measure.attackPaths.length > 0 &&
          attackPathsData.length > 0
        ) {
          const matchedPaths = attackPathsData.filter((path) =>
            measure.attackPaths.some((mId) => attackPathIdsMatch(mId, path.id)),
          );
          if (matchedPaths.length > 0) return matchedPaths;
        }
        if (measure.attackPathId && attackPathsData.length > 0) {
          const matchedPaths = attackPathsData.filter((path) =>
            attackPathIdsMatch(measure.attackPathId, path.id),
          );
          if (matchedPaths.length > 0) return matchedPaths;
        }
        return [];
      };

      const enrichedMeasures = ecosystemMeasures.map((measure) => ({
        ...measure,
        attackPathsDetails: getAssociatedAttackPaths(measure),
      }));

      const residualRisks = calculateResidualRisks(
        stakeholdersData,
        attackPathsData,
        ecosystemMeasures,
      );

      const generator = new EcosystemMeasuresPdfGenerator(
        {
          measures: enrichedMeasures,
          residualRisks: residualRisks,
          analysisName: currentAnalysis?.name,
          organizationName: currentAnalysis?.companyName,
          analysisId: currentAnalysis?.id,
        },
        currentAnalysis,
      );

      const blob = await generator.generateReport();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `mesures-securite-${currentAnalysis?.name || "export"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      dismissToast(toastId);
      showSuccessToast("Rapport PDF généré avec succès");
    } catch (error) {
      console.error("Error generating PDF:", error);
      dismissToast(toastId);
      showErrorToast("Erreur lors de la génération du PDF");
    }
  };

  const handleExportPDFEn = async () => {
    const toastId = showLoadingToast("Generating PDF Report...");
    try {
      const [pathsResponse, stakeholdersResponse] = await Promise.all([
        workshop3Service.getAttackPaths(currentAnalysis.id),
        api.get(`/analyses/${currentAnalysis.id}/atelier3/stakeholders`),
      ]);

      let attackPathsData = [];
      if (pathsResponse.success && pathsResponse.data) {
        attackPathsData = Array.isArray(pathsResponse.data)
          ? pathsResponse.data
          : [];
      }

      let stakeholdersData = [];
      if (stakeholdersResponse.success && stakeholdersResponse.data) {
        stakeholdersData = Array.isArray(stakeholdersResponse.data)
          ? stakeholdersResponse.data
          : [];
      }

      const normalizeAttackPathId = (id) => {
        if (!id) return "";
        const parts = id.split("-");
        if (parts.length > 1) {
          const lastPart = parts[parts.length - 1];
          if (lastPart.length >= 13 && /^\d+$/.test(lastPart)) {
            return parts.slice(0, -1).join("-");
          }
        }
        return id;
      };

      const attackPathIdsMatch = (id1, id2) => {
        if (!id1 || !id2) return false;
        if (id1 === id2) return true;
        const normalized1 = normalizeAttackPathId(id1);
        const normalized2 = normalizeAttackPathId(id2);
        return normalized1 === normalized2 && normalized1.length > 0;
      };

      const getAssociatedAttackPaths = (measure) => {
        if (!measure) return [];
        if (
          measure.attackPaths &&
          measure.attackPaths.length > 0 &&
          attackPathsData.length > 0
        ) {
          const matchedPaths = attackPathsData.filter((path) =>
            measure.attackPaths.some((mId) => attackPathIdsMatch(mId, path.id)),
          );
          if (matchedPaths.length > 0) return matchedPaths;
        }
        return [];
      };

      const enrichedMeasures = ecosystemMeasures.map((measure) => ({
        ...measure,
        attackPathsDetails: getAssociatedAttackPaths(measure),
      }));

      const { default: EcosystemMeasuresPdfGenerator } =
        await import("../../../utils/ecosystemMeasuresPdfGenerator");

      const residualRisks = calculateResidualRisks(
        stakeholdersData,
        attackPathsData,
        ecosystemMeasures,
      );

      const generator = new EcosystemMeasuresPdfGenerator(
        {
          measures: enrichedMeasures,
          residualRisks: residualRisks,
          analysisName: currentAnalysis?.name,
          organizationName: currentAnalysis?.companyName,
          analysisId: currentAnalysis?.id,
        },
        currentAnalysis,
        "en",
      );

      const blob = await generator.generateReport();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `security-measures-${currentAnalysis?.name || "export"}-EN.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      dismissToast(toastId);
      showSuccessToast("PDF Report (EN) generated successfully");
    } catch (error) {
      console.error("Error generating PDF (EN):", error);
      dismissToast(toastId);
      showErrorToast("Error generating PDF (EN)");
    }
  };

  // Handle DOCX Export
  const handleExportDocx = async () => {
    const toastId = showLoadingToast("Génération du rapport Word...");
    try {
      const [pathsResponse, stakeholdersResponse] = await Promise.all([
        workshop3Service.getAttackPaths(currentAnalysis.id),
        api.get(`/analyses/${currentAnalysis.id}/atelier3/stakeholders`),
      ]);

      const attackPathsData = pathsResponse.success ? pathsResponse.data : [];
      const stakeholdersData = stakeholdersResponse.success
        ? stakeholdersResponse.data
        : [];
      const residualRisks = calculateResidualRisks(
        stakeholdersData,
        attackPathsData,
        ecosystemMeasures,
      );

      const normalizeAttackPathId = (id) => {
        if (!id) return "";
        const parts = id.split("-");
        if (parts.length > 1) {
          const lastPart = parts[parts.length - 1];
          if (lastPart.length >= 13 && /^\d+$/.test(lastPart)) {
            return parts.slice(0, -1).join("-");
          }
        }
        return id;
      };

      const attackPathIdsMatch = (id1, id2) => {
        if (!id1 || !id2) return false;
        if (id1 === id2) return true;
        const normalized1 = normalizeAttackPathId(id1);
        const normalized2 = normalizeAttackPathId(id2);
        return normalized1 === normalized2 && normalized1.length > 0;
      };

      const getAssociatedAttackPaths = (measure) => {
        if (!measure) return [];
        if (
          measure.attackPaths &&
          measure.attackPaths.length > 0 &&
          attackPathsData.length > 0
        ) {
          const matchedPaths = attackPathsData.filter((path) =>
            measure.attackPaths.some((mId) => attackPathIdsMatch(mId, path.id)),
          );
          if (matchedPaths.length > 0) return matchedPaths;
        }
        return [];
      };

      const enrichedMeasures = ecosystemMeasures.map((measure) => ({
        ...measure,
        attackPathsDetails: getAssociatedAttackPaths(measure),
      }));

      await generateEcosystemMeasuresDocx(
        {
          measures: enrichedMeasures,
          residualRisks,
          analysisName: currentAnalysis?.name,
          organizationName: currentAnalysis?.companyName,
          analysisId: currentAnalysis?.id,
        },
        currentAnalysis,
      );

      dismissToast(toastId);
      showSuccessToast("Rapport Word généré avec succès");
    } catch (error) {
      console.error("Error generating DOCX:", error);
      dismissToast(toastId);
      showErrorToast("Erreur lors de la génération du document Word");
    }
  };

  // Handle English DOCX Export
  const handleExportDocxEn = async () => {
    const toastId = showLoadingToast("Generating Word Report...");
    try {
      const [pathsResponse, stakeholdersResponse] = await Promise.all([
        workshop3Service.getAttackPaths(currentAnalysis.id),
        api.get(`/analyses/${currentAnalysis.id}/atelier3/stakeholders`),
      ]);

      const attackPathsData = pathsResponse.success ? pathsResponse.data : [];
      const stakeholdersData = stakeholdersResponse.success
        ? stakeholdersResponse.data
        : [];
      const residualRisks = calculateResidualRisks(
        stakeholdersData,
        attackPathsData,
        ecosystemMeasures,
      );

      const normalizeAttackPathId = (id) => {
        if (!id) return "";
        const parts = id.split("-");
        if (parts.length > 1) {
          const lastPart = parts[parts.length - 1];
          if (lastPart.length >= 13 && /^\d+$/.test(lastPart)) {
            return parts.slice(0, -1).join("-");
          }
        }
        return id;
      };

      const attackPathIdsMatch = (id1, id2) => {
        if (!id1 || !id2) return false;
        if (id1 === id2) return true;
        const normalized1 = normalizeAttackPathId(id1);
        const normalized2 = normalizeAttackPathId(id2);
        return normalized1 === normalized2 && normalized1.length > 0;
      };

      const getAssociatedAttackPaths = (measure) => {
        if (!measure) return [];
        if (
          measure.attackPaths &&
          measure.attackPaths.length > 0 &&
          attackPathsData.length > 0
        ) {
          const matchedPaths = attackPathsData.filter((path) =>
            measure.attackPaths.some((mId) => attackPathIdsMatch(mId, path.id)),
          );
          if (matchedPaths.length > 0) return matchedPaths;
        }
        return [];
      };

      const enrichedMeasures = ecosystemMeasures.map((measure) => ({
        ...measure,
        attackPathsDetails: getAssociatedAttackPaths(measure),
      }));

      await generateEcosystemMeasuresDocx(
        {
          measures: enrichedMeasures,
          residualRisks,
          analysisName: currentAnalysis?.name,
          organizationName: currentAnalysis?.companyName,
          analysisId: currentAnalysis?.id,
        },
        currentAnalysis,
        "en",
      );

      dismissToast(toastId);
      showSuccessToast("Word Report (EN) generated successfully");
    } catch (error) {
      console.error("Error generating DOCX (EN):", error);
      dismissToast(toastId);
      showErrorToast("Error generating Word (EN) document");
    }
  };

  // Handle French CSV export
  const handleExportCsvFr = async () => {
    try {
      const [attackPathsResponse, stakeholdersResponse] = await Promise.all([
        workshop3Service.getAttackPaths(currentAnalysis?.id),
        api.get(`/analyses/${currentAnalysis?.id}/atelier3/stakeholders`),
      ]);
      const attackPaths = attackPathsResponse?.data || [];
      const stakeholdersData = stakeholdersResponse?.success && stakeholdersResponse?.data ? stakeholdersResponse.data : [];
      
      const sourceRisksMap = {};
      const objectifsMap = {};
      
      attackPaths.forEach(path => {
        if (path.sourceRiskId) {
           sourceRisksMap[path.sourceRiskId] = path.sourceRiskName || path.sourceRiskId;
        }
        if (path.objectifVise) {
           objectifsMap[path.objectifVise] = path.objectifVise;
        }
      });

      const formatListText = (items, dictionary) => {
        if (Array.isArray(items)) {
          return items
            .map((item) => {
              if (item && typeof item === "object") {
                return item.name || item.riskName || item.sourceRiskId || item.objectifVise || item.id || "";
              }
              return dictionary[item] || item;
            })
            .filter(Boolean)
            .join(", ");
        }
        return typeof items === "string" ? items : "";
      };

      const normalizeAttackPathId = (id) => {
        if (!id) return "";
        const parts = id.split("-");
        if (parts.length > 1) {
          const lastPart = parts[parts.length - 1];
          if (lastPart.length >= 13 && /^\d+$/.test(lastPart)) {
            return parts.slice(0, -1).join("-");
          }
        }
        return id;
      };

      const attackPathIdsMatch = (id1, id2) => {
        if (!id1 || !id2) return false;
        if (id1 === id2) return true;
        const normalized1 = normalizeAttackPathId(id1);
        const normalized2 = normalizeAttackPathId(id2);
        return normalized1 === normalized2 && normalized1.length > 0;
      };

      const getAssociatedAttackPaths = (measure) => {
        if (!measure) return [];
        if (measure.attackPathsDetails && measure.attackPathsDetails.length > 0)
          return measure.attackPathsDetails;
        if (
          measure.attackPaths &&
          measure.attackPaths.length > 0 &&
          attackPaths.length > 0
        ) {
          return attackPaths.filter((path) =>
            measure.attackPaths.some((mId) => attackPathIdsMatch(mId, path.id)),
          );
        }
        if (measure.attackPathId && attackPaths.length > 0) {
          return attackPaths.filter((path) =>
            attackPathIdsMatch(measure.attackPathId, path.id),
          );
        }
        return [];
      };

      const measuresForExport = ecosystemMeasures.map((m) => {
        const objVisesList = m.objectifsVises && m.objectifsVises.length > 0 ? m.objectifsVises : m.objectives;
        const associatedPaths = getAssociatedAttackPaths(m);
        
        // Derive sourceRisks and objectives from paths to match PDF behavior
        let derivedSourceRisks = [...new Set(associatedPaths.map((p) => p.sourceRiskName || p.sourceRiskId).filter(Boolean))];
        let derivedObjectives = [...new Set(associatedPaths.map((p) => p.objectifVise).filter(Boolean))];
        
        // If not empty, use the derived arrays. Otherwise, fallback to the previous mapping
        const finalSourceRisks = derivedSourceRisks.length > 0 
            ? derivedSourceRisks.join(", ") 
            : formatListText(m.sourceRisks, sourceRisksMap);
            
        const finalObjectives = derivedObjectives.length > 0 
            ? derivedObjectives.join(", ") 
            : formatListText(objVisesList, objectifsMap);
        
        return {
          ...m,
          title: m.title?.fr || m.title?.en || m.title || m.name || "",
          sourceRisks: finalSourceRisks,
          objectives: finalObjectives,
          objectifsVises: undefined // Force generator to use the correctly formatted string
        };
      });

      const residualRisksCsv = calculateResidualRisks(
        stakeholdersData,
        attackPaths,
        ecosystemMeasures,
      );

      const generator = new Workshop3Activite4CsvGenerator(
        {
          measures: measuresForExport,
          residualRisks: residualRisksCsv,
          organizationName: currentAnalysis?.companyName || "Organisation",
        },
        "fr",
      );
      generator.download(
        `EBIOS-RM_Workshop3_Activite4_${currentAnalysis?.companyName || "Organisation"}_${new Date().toISOString().split("T")[0]}.xls`,
      );
      showSuccessToast("Export CSV (FR) généré avec succès");
    } catch (error) {
      console.error("CSV Export error", error);
      showErrorToast("Erreur lors de l'export CSV");
    }
  };

  // Handle English CSV export
  const handleExportCsvEn = async () => {
    try {
      const [attackPathsResponse, stakeholdersResponse2] = await Promise.all([
        workshop3Service.getAttackPaths(currentAnalysis?.id),
        api.get(`/analyses/${currentAnalysis?.id}/atelier3/stakeholders`),
      ]);
      const attackPaths = attackPathsResponse?.data || [];
      const stakeholdersDataEn = stakeholdersResponse2?.success && stakeholdersResponse2?.data ? stakeholdersResponse2.data : [];
      
      const sourceRisksMap = {};
      const objectifsMap = {};
      
      attackPaths.forEach(path => {
        if (path.sourceRiskId) {
           sourceRisksMap[path.sourceRiskId] = path.sourceRiskName || path.sourceRiskId;
        }
        if (path.objectifVise) {
           objectifsMap[path.objectifVise] = path.objectifVise;
        }
      });

      const formatListText = (items, dictionary) => {
        if (Array.isArray(items)) {
          return items
            .map((item) => {
              if (item && typeof item === "object") {
                return item.name || item.riskName || item.sourceRiskId || item.objectifVise || item.id || "";
              }
              return dictionary[item] || item;
            })
            .filter(Boolean)
            .join(", ");
        }
        return typeof items === "string" ? items : "";
      };

      const normalizeAttackPathId = (id) => {
        if (!id) return "";
        const parts = id.split("-");
        if (parts.length > 1) {
          const lastPart = parts[parts.length - 1];
          if (lastPart.length >= 13 && /^\d+$/.test(lastPart)) {
            return parts.slice(0, -1).join("-");
          }
        }
        return id;
      };

      const attackPathIdsMatch = (id1, id2) => {
        if (!id1 || !id2) return false;
        if (id1 === id2) return true;
        const normalized1 = normalizeAttackPathId(id1);
        const normalized2 = normalizeAttackPathId(id2);
        return normalized1 === normalized2 && normalized1.length > 0;
      };

      const getAssociatedAttackPaths = (measure) => {
        if (!measure) return [];
        if (measure.attackPathsDetails && measure.attackPathsDetails.length > 0)
          return measure.attackPathsDetails;
        if (
          measure.attackPaths &&
          measure.attackPaths.length > 0 &&
          attackPaths.length > 0
        ) {
          return attackPaths.filter((path) =>
            measure.attackPaths.some((mId) => attackPathIdsMatch(mId, path.id)),
          );
        }
        if (measure.attackPathId && attackPaths.length > 0) {
          return attackPaths.filter((path) =>
            attackPathIdsMatch(measure.attackPathId, path.id),
          );
        }
        return [];
      };

      const measuresForExport = ecosystemMeasures.map((m) => {
        const objVisesList = m.objectifsVises && m.objectifsVises.length > 0 ? m.objectifsVises : m.objectives;
        const associatedPaths = getAssociatedAttackPaths(m);
        
        // Derive sourceRisks and objectives from paths to match PDF behavior
        let derivedSourceRisks = [...new Set(associatedPaths.map((p) => p.sourceRiskName || p.sourceRiskId).filter(Boolean))];
        let derivedObjectives = [...new Set(associatedPaths.map((p) => p.objectifVise).filter(Boolean))];
        
        // If not empty, use the derived arrays. Otherwise, fallback to the previous mapping
        const finalSourceRisks = derivedSourceRisks.length > 0 
            ? derivedSourceRisks.join(", ") 
            : formatListText(m.sourceRisks, sourceRisksMap);
            
        const finalObjectives = derivedObjectives.length > 0 
            ? derivedObjectives.join(", ") 
            : formatListText(objVisesList, objectifsMap);
        
        return {
          ...m,
          title: m.title?.en || m.title?.fr || m.title || m.name || "",
          sourceRisks: finalSourceRisks,
          objectives: finalObjectives,
          objectifsVises: undefined // Force generator to use the correctly formatted string
        };
      });

      const residualRisksCsvEn = calculateResidualRisks(
        stakeholdersDataEn,
        attackPaths,
        ecosystemMeasures,
      );

      const generator = new Workshop3Activite4CsvGenerator(
        {
          measures: measuresForExport,
          residualRisks: residualRisksCsvEn,
          organizationName: currentAnalysis?.companyName || "Organisation",
        },
        "en",
      );
      generator.download(
        `EBIOS-RM_Workshop3_Activity4_${currentAnalysis?.companyName || "Organisation"}_${new Date().toISOString().split("T")[0]}.xls`,
      );
      showSuccessToast("CSV Export (EN) generated successfully");
    } catch (error) {
      console.error("CSV Export error", error);
      showErrorToast("Error during CSV export");
    }
  };

  return (
    <div ref={formChangeRef} className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="flex items-center text-sm text-slate-500 mb-2">
              <span className="hover:text-blue-600 transition-colors">
                {t("workshop3.navigationTitle")}
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mx-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              <span className="text-blue-600 font-medium">
                {t("workshop3.activity4.breadcrumb")}
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center">
              <Shield size={28} className="mr-3 text-blue-600" />
              {t("workshop3.activity4.title")}
            </h1>
          </div>

          <div className="flex items-center space-x-3">
            {/* Unified Export Menu */}
            <WorkshopExportMenu
              onExportPdfFr={handleExportPDF}
              onExportPdfEn={handleExportPDFEn}
              onExportDocxFr={handleExportDocx}
              onExportDocxEn={handleExportDocxEn}
              onExportCsvFr={handleExportCsvFr}
              onExportCsvEn={handleExportCsvEn}
            />

            {/* Save Button */}
            <Button
              variant="primary"
              color="blue"
              icon={<Save />}
              onClick={saveMeasures}
              title={t("common.save")}
            >
              {t("common.save")}
            </Button>

            {/* Help Button */}
            <Button
              variant="primary"
              color="blue"
              icon={<Info />}
              onClick={() => setShowGuideModal(true)}
              title={t("common.help")}
            >
              {t("common.help")}
            </Button>

            {/* AI Assistant Button (Old Look) */}
            {companyAiEnabled && (
            <button
              onClick={() => setShowAIModal(!showAIModal)}
              className={`text-sm font-medium p-2 rounded-lg flex items-center justify-center shadow-sm transition duration-200 ${
                showAIModal
                  ? "bg-purple-600 text-white hover:bg-purple-700"
                  : "bg-purple-100 text-purple-700 hover:bg-purple-200"
              }`}
              title="Assistant ACG AI"
            >
              <BrainCircuit size={20} />
            </button>
            )}
          </div>
        </div>

        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
          <p className="text-sm text-blue-700">
            {t("workshop3.activity4.description")}
          </p>
        </div>
      </div>

      <GuideModal
        isOpen={showGuideModal}
        onClose={() => setShowGuideModal(false)}
      />
      <InlineAiSuggestions
        isVisible={showAIModal}
        onClose={() => setShowAIModal(false)}
        onGenerate={handleAIGenerated}
        analysisId={currentAnalysis?.id}
      />

      {/* Tab bar */}
      <div className="flex border-b border-gray-200 bg-white rounded-t-xl shadow-sm px-2">
        <button
          type="button"
          onClick={() => setActiveTab("measures")}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "measures"
              ? "border-blue-600 text-blue-700"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
          }`}
        >
          <Shield size={16} />
          Mesures de Sécurité
          <span
            className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
              activeTab === "measures"
                ? "bg-blue-100 text-blue-700"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {ecosystemMeasures.length}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("risks")}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "risks"
              ? "border-indigo-600 text-indigo-700"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
          }`}
        >
          <BarChart2 size={16} />
          Risque par Partie Prenante
        </button>
        <div className="ml-auto flex items-center pr-2">
          <Button
            variant="primary"
            color="blue"
            icon={<Plus />}
            onClick={() => {
              setEditingMeasure(null);
              setShowAddForm(true);
            }}
          >
            {t('common.add')}
          </Button>
        </div>
      </div>

      {showAddForm &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-start justify-center overflow-auto">
            <div
              className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
              onClick={() => {
                setShowAddForm(false);
                setEditingMeasure(null);
              }}
            />
            <div className="relative w-full max-w-5xl my-8 mx-auto animate-in fade-in zoom-in-95 duration-200 z-[10000]">
              <MeasureForm
                measure={editingMeasure}
                onSave={handleSaveMeasure}
                onCancel={() => {
                  setShowAddForm(false);
                  setEditingMeasure(null);
                }}
                analysisId={currentAnalysis?.id}
              />
            </div>
          </div>,
          document.body,
        )}

      {/* Tab content */}
      <div className="bg-white rounded-b-xl shadow-sm border border-t-0 border-gray-200 p-4">
        {activeTab === "measures" && (
          <MeasureTable
            measures={ecosystemMeasures}
            onEdit={(measure) => {
              setEditingMeasure(measure);
              setShowAddForm(true);
            }}
            onDelete={handleDeleteMeasure}
            onBulkDelete={handleBulkDeleteMeasures}
            isLoading={isLoading}
          />
        )}
        {activeTab === "risks" && (
          <StakeholderRiskView
            measures={ecosystemMeasures}
            analysisId={currentAnalysis?.id}
          />
        )}
      </div>
    </div>
  );
};

export default Activite4;
